import React, { useEffect } from 'react';
import { DatePicker, Form, Input, InputNumber, Select } from 'antd';
import { ROUTE_EXPENSE_TYPE_OPTIONS } from '../../constants/routeExpenseTypes';
import {
  mergeRouteExpenseFormValues,
  type RouteExpenseFormValues
} from '../../domain/routeExpenseFormValues';

export type RouteExpenseFormMode = 'create' | 'edit' | 'readonly' | 'approval';

export type RouteExpenseFormProps = {
  mode?: RouteExpenseFormMode;
  values?: Partial<RouteExpenseFormValues>;
  onChange?: (values: RouteExpenseFormValues) => void;
  /** When true, renders inside parent Form.Item context (modal). */
  useAntForm?: boolean;
  form?: ReturnType<typeof Form.useForm<RouteExpenseFormValues>>[0];
  compact?: boolean;
  showInvoice?: boolean;
  showComment?: boolean;
};

export const RouteExpenseForm: React.FC<RouteExpenseFormProps> = ({
  mode = 'create',
  values: controlledValues,
  onChange,
  useAntForm = false,
  form: externalForm,
  compact = false,
  showInvoice = true,
  showComment = true
}) => {
  const [internalForm] = Form.useForm<RouteExpenseFormValues>();
  const form = externalForm ?? internalForm;
  const readonly = mode === 'readonly' || mode === 'approval';

  useEffect(() => {
    if (!useAntForm && controlledValues) {
      form.setFieldsValue(mergeRouteExpenseFormValues(controlledValues));
    }
  }, [useAntForm, controlledValues, form]);

  useEffect(() => {
    if (useAntForm) return;
    if (!onChange) return;
    onChange?.(mergeRouteExpenseFormValues(form.getFieldsValue(true) as RouteExpenseFormValues));
  }, [form, onChange, useAntForm]);

  const fields = (
    <>
      <Form.Item
        name="type"
        label="Тип расхода"
        rules={readonly ? [] : [{ required: true, message: 'Выберите тип' }]}
      >
        <Select
          disabled={readonly}
          options={[...ROUTE_EXPENSE_TYPE_OPTIONS]}
          placeholder="Тип"
        />
      </Form.Item>
      <Form.Item name="description" label="Описание">
        <Input.TextArea
          disabled={readonly}
          rows={compact ? 1 : 2}
          placeholder="Назначение расхода"
        />
      </Form.Item>
      <Form.Item
        name="amountWithVAT"
        label="Сумма с НДС (₽)"
        rules={readonly ? [] : [{ required: true, message: 'Укажите сумму' }]}
      >
        <InputNumber
          disabled={readonly}
          min={0.01}
          step={100}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item name="vatRate" label="Ставка НДС (%)">
        <InputNumber
          disabled={readonly}
          min={0}
          max={100}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item name="expenseDate" label="Дата расхода">
        <DatePicker
          disabled={readonly}
          style={{ width: '100%' }}
          format="DD.MM.YYYY"
        />
      </Form.Item>
      {showInvoice ? (
        <Form.Item name="invoiceId" label="ID счёта (необязательно)">
          <InputNumber
            disabled={readonly}
            min={1}
            style={{ width: '100%' }}
            placeholder="Если расход по счёту"
          />
        </Form.Item>
      ) : null}
      {showComment ? (
        <Form.Item name="comment" label="Комментарий">
          <Input disabled={readonly} placeholder="Доп. пояснение для бухгалтерии" />
        </Form.Item>
      ) : null}
    </>
  );

  if (useAntForm) {
    return (
      <Form form={form} layout="vertical" initialValues={mergeRouteExpenseFormValues(controlledValues)}>
        {fields}
      </Form>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={mergeRouteExpenseFormValues(controlledValues)}
      onValuesChange={(_, all) => onChange?.(mergeRouteExpenseFormValues(all as RouteExpenseFormValues))}
    >
      {fields}
    </Form>
  );
};

export default RouteExpenseForm;
